import React from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import { getRequest, postRequest } from '../../../lib/apiUtils';
import { formatCurrency } from '../../../lib/utils';
import { clearRecipientSummaryCache, getCachedRecipientSummary, setCachedRecipientSummary } from '../../../lib/recipientSummaryCache';
import AiIcon from '../../commonComponents/shared/AiIcon';

export { clearRecipientSummaryCache };

const GAP = 8;


export class RecipientSummaryCard extends React.Component {

    state = {
        show: false,
        position: null,
        summary: null,
        loading: false,
        error: false,
        aiLoading: false
    };

    requestId = 0;
    triggerRef = React.createRef();
    cardRef = React.createRef();

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.showAiSummaries && !this.props.showAiSummaries) {
            if (this.state.show) {
                this.close();
                return;
            }

            if (this.state.aiLoading) this.setState({ aiLoading: false });
        }

        const cardContentChanged = !prevState.show
            || prevState.loading !== this.state.loading
            || prevState.error !== this.state.error
            || prevState.summary !== this.state.summary
            || prevState.aiLoading !== this.state.aiLoading
            || prevProps.title !== this.props.title;

        if (this.state.show && cardContentChanged) this.updatePosition();
    }

    componentWillUnmount() {
        this.requestId++;
        this.removeWindowListeners();
    }

    addWindowListeners = () => {
        document.addEventListener('mousedown', this.onDocumentPointerDown);
        document.addEventListener('touchstart', this.onDocumentPointerDown);
        window.addEventListener('scroll', this.close, true);
        window.addEventListener('resize', this.close);
        window.addEventListener('keydown', this.onKeyDown);
    };

    removeWindowListeners = () => {
        document.removeEventListener('mousedown', this.onDocumentPointerDown);
        document.removeEventListener('touchstart', this.onDocumentPointerDown);
        window.removeEventListener('scroll', this.close, true);
        window.removeEventListener('resize', this.close);
        window.removeEventListener('keydown', this.onKeyDown);
    };

    onKeyDown = e => {
        if (e.key === 'Escape') this.close();
    };

    onDocumentPointerDown = e => {
        if ((this.triggerRef.current && this.triggerRef.current.contains(e.target))
            || (this.cardRef.current && this.cardRef.current.contains(e.target))) {
            return;
        }

        this.close();
    };

    onTriggerClick = e => {
        e.preventDefault();
        e.stopPropagation();
        if (this.state.show) {
            this.close();
            return;
        }

        this.open();
    };

    // The card renders in a portal, but React still bubbles its events to the
    // table row's hover/click handlers - block them at the card boundary.
    stopEvent = e => {
        e.stopPropagation();
    };

    getPosition = () => {
        if (!this.triggerRef.current || !this.cardRef.current) return null;

        const triggerRect = this.triggerRef.current.getBoundingClientRect();
        const cardRect = this.cardRef.current.getBoundingClientRect();
        const flipUp = triggerRect.bottom + GAP + cardRect.height > window.innerHeight;
        const position = {
            left: Math.max(GAP, Math.min(triggerRect.left, window.innerWidth - cardRect.width - GAP))
        };

        if (flipUp) {
            position.bottom = window.innerHeight - triggerRect.top + GAP;
        } else {
            position.top = triggerRect.bottom + GAP;
        }

        return position;
    };

    updatePosition = () => {
        const position = this.getPosition();
        if (!position) return;

        this.setState({ position });
    };

    open = () => {
        this.setState({ show: true, position: null });
        if (this.props.onOpenChange) this.props.onOpenChange(true);
        this.addWindowListeners();
        this.loadSummary();
    };

    close = () => {
        this.requestId++;
        this.removeWindowListeners();
        this.setState({ show: false, loading: false, aiLoading: false });
        if (this.props.onOpenChange) this.props.onOpenChange(false);
    };

    loadSummary = async () => {
        const { recipientId } = this.props;

        const cached = getCachedRecipientSummary(recipientId);
        if (cached) {
            this.setState({ summary: cached, error: false });
            if (cached.aiSummaryPending && !cached.aiSummary) {
                this.generateAiSummary();
            }
            return;
        }

        this.setState({ loading: true, error: false, summary: null });
        const requestId = ++this.requestId;

        const result = await getRequest(`/applications/recipients/${recipientId}/summary`);
        if (requestId !== this.requestId) return;

        if (!result.success) {
            this.setState({ loading: false, error: true });
            return;
        }

        setCachedRecipientSummary(recipientId, result.data);
        this.setState({ summary: result.data, loading: false });

        if (result.data.aiSummaryPending && !result.data.aiSummary) {
            this.generateAiSummary();
        }
    };

    generateAiSummary = async () => {
        const { recipientId } = this.props;

        this.setState({ aiLoading: true });
        const requestId = this.requestId;

        const result = await postRequest(`/applications/recipients/${recipientId}/summary/generate`);
        if (requestId !== this.requestId || !this.props.showAiSummaries) return;

        const updated = {
            ...(this.state.summary || getCachedRecipientSummary(recipientId) || {}),
            aiSummary: result.success ? result.data.aiSummary : null,
            aiSummaryGeneratedDate: result.success ? result.data.generatedDate : null,
            aiSummaryPending: !result.success
        };
        setCachedRecipientSummary(recipientId, updated);

        this.setState({ summary: updated, aiLoading: false });
    };


    getHistoryDuration = (firstApplicationDate, lastApplicationDate) => {
        const first = new Date(firstApplicationDate);
        const last = new Date(lastApplicationDate);
        let months = ((last.getUTCFullYear() - first.getUTCFullYear()) * 12)
            + last.getUTCMonth() - first.getUTCMonth();

        months = Math.max(0, months);

        const years = Math.floor(months / 12);
        const remainingMonths = months % 12;
        const parts = [];
        if (years > 0) parts.push(`${years} ${years === 1 ? 'year' : 'years'}`);
        if (remainingMonths > 0) parts.push(`${remainingMonths} ${remainingMonths === 1 ? 'month' : 'months'}`);
        return parts.length > 0 ? parts.join(', ') : 'Less than a month';
    };

    formatSummaryDate = date => new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC'
    });

    formatSummaryCurrency = (amount, isIsraeli) =>
        formatCurrency(Math.round(Number(amount)), isIsraeli).replace(/\.00$/, '');

    renderStats = stats => {
        const isIsraeli = stats.isIsraeli;

        if (stats.totalApplications === 1) {
            return (
                <div className="recipient-summary-stats recipient-summary-first-application">
                    <div className="stat full-width">
                        <p className="stat-label">First Application</p>
                        <p className="stat-note">No prior application history</p>
                    </div>
                    {stats.totalPaid > 0 && <div className="stat full-width">
                        <p className="stat-label">Total Paid</p>
                        <p className="stat-value">{this.formatSummaryCurrency(stats.totalPaid, isIsraeli)}</p>
                    </div>}
                </div>
            );
        }

        const historyDuration = this.getHistoryDuration(stats.firstApplicationDate, stats.lastApplicationDate);
        return (
            <div className="recipient-summary-history">
                <p className="history-overview">
                    <strong>{stats.totalApplications} applications</strong>
                    <span> {'\u00b7'} </span>
                    <strong>{this.formatSummaryCurrency(stats.totalPaid, isIsraeli)} paid</strong>
                </p>
                <div className="history-section">
                    <p className="history-heading"><strong>History</strong> <span>{'\u00b7'}</span> <strong>{historyDuration}</strong></p>
                    {stats.latestPaidApplicationDate && <p><strong>Last Paid</strong> {'\u2014'} <strong>{this.formatSummaryDate(stats.latestPaidApplicationDate)}</strong>
                        {stats.latestPaidApplicationReason && ` \u00b7 ${stats.latestPaidApplicationReason}`}
                        {stats.latestPaidApplicationAmount != null && <React.Fragment> {'\u00b7'} <strong>{this.formatSummaryCurrency(stats.latestPaidApplicationAmount, isIsraeli)}</strong></React.Fragment>}
                    </p>}
                </div>
            </div>
        );
    };

    renderShimmer = lines => (
        <div aria-busy="true">
            {Array.from({ length: lines }).map((_, i) => (
                <div key={i} className="shimmer-line" style={i === lines - 1 ? { width: '60%' } : null} />
            ))}
        </div>
    );

    renderCardBody = () => {
        const { summary, loading, error, aiLoading } = this.state;

        if (error) {
            return <p className="recipient-summary-error">Unable to load summary</p>;
        }

        if (loading || !summary) {
            return this.renderShimmer(3);
        }

        if (!summary.stats || summary.stats.totalApplications === 0) {
            return <p className="recipient-summary-error">No applications visible to you</p>;
        }

        return (
            <React.Fragment>
                {this.renderStats(summary.stats)}
                {(aiLoading || summary.aiSummary) &&
                    <div className="recipient-summary-ai">
                        <p className="stat-label">AI Summary</p>
                        {aiLoading ? this.renderShimmer(2) : <p>{summary.aiSummary}</p>}
                    </div>}
            </React.Fragment>
        );
    };

    render() {
        const { recipientId, disabled, title, children } = this.props;
        const { show, position } = this.state;

        if (!this.props.showAiSummaries || disabled || !recipientId) {
            return children || null;
        }

        const cardId = `recipient-summary-${recipientId}`;

        return (
            <span className="recipient-summary-trigger">
                {children}
                <button
                    type="button"
                    ref={this.triggerRef}
                    className="recipient-summary-button"
                    aria-controls={cardId}
                    aria-expanded={show}
                    aria-label="Show recipient summary"
                    title="Show recipient summary"
                    onClick={this.onTriggerClick}
                    onMouseDown={this.stopEvent}
                >
                    <AiIcon className="recipient-summary-button-icon" />
                </button>
                {show && ReactDOM.createPortal(
                    <div id={cardId} ref={this.cardRef} className="recipient-summary-card" style={position} role="dialog"
                        aria-label={title || 'AI summary'}
                        onMouseOver={this.stopEvent} onMouseOut={this.stopEvent}
                        onClick={this.stopEvent} onDoubleClick={this.stopEvent}
                        onMouseDown={this.stopEvent}>
                        {title && <p className="card-header">{title}</p>}
                        {this.renderCardBody()}
                    </div>,
                    document.body)}
            </span>
        );
    }
}

function mapStateToProps(state) {
    return {
        showAiSummaries: state.userProfile.profile.data.showAiSummaries !== false
    };
}

export default connect(mapStateToProps)(RecipientSummaryCard);



// WEBPACK FOOTER //
// ./src/components/applicationsComponents/applications/RecipientSummaryCard.jsx